import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Table, Tag, Button, Space, Popconfirm, message, Tooltip, Modal, Input, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, CheckOutlined,
  EyeOutlined, BranchesOutlined, StopOutlined, FontSizeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';

const STATUS_COLOR = { draft: 'orange', published: 'green', inactive: 'default' };
const STATUS_LABEL = { draft: 'Draft', published: 'Published', inactive: 'Inactive' };

export default function EvalFormsPage() {
  const currentUser = useContext(UserContext);
  const canEdit     = (currentUser?.CanConfigEvals ?? 0) >= 2;
  // Rename renames every version of a form (they share the name): admins only.
  const canRename   = currentUser?.Role === 'admin';

  const [forms, setForms]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [statusFilter, setStatusFilter] = useState(['draft', 'published']);
  const [typeFilter,   setTypeFilter]   = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState('evaluation');
  const [createName, setCreateName] = useState('');
  const [creating, setCreating]     = useState(false);
  const [cloneOpen, setCloneOpen]   = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneName, setCloneName]   = useState('');
  const [cloning, setCloning]       = useState(false);
  const [renameSource, setRenameSource] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming]     = useState(false);
  const navigate = useNavigate();

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/eval-forms');
      setForms(res.data);
    } catch {
      message.error('Failed to load evaluation forms.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const openCreate = (formType) => {
    setCreateType(formType);
    setCreateName('');
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) { message.warning('Please enter a form name.'); return; }
    setCreating(true);
    try {
      const res = await api.post('/api/eval-forms', { name, form_type: createType });
      setCreateOpen(false);
      navigate(`/eval-forms/${res.data.id}/edit`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create form.');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (formId) => {
    try {
      await api.post(`/api/eval-forms/${formId}/publish`);
      message.success('Form published.');
      fetchForms();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to publish.');
    }
  };

  // New Version — same name, next version number
  const handleNewVersion = async (formId) => {
    try {
      const res = await api.post(`/api/eval-forms/${formId}/copy`);
      message.success('Draft created — ready to edit.');
      navigate(`/eval-forms/${res.data.id}/edit`);
    } catch (err) {
      if (err.response?.data?.draft_id) {
        message.info('A draft already exists — opening it.');
        navigate(`/eval-forms/${err.response.data.draft_id}/edit`);
      } else {
        message.error(err.response?.data?.error || 'Failed to create new version.');
      }
    }
  };

  // Copy — new name, resets to v1
  const openClone = (record) => {
    setCloneSource(record);
    setCloneName(`${record.Name} (Copy)`);
    setCloneOpen(true);
  };

  const handleClone = async () => {
    const name = cloneName.trim();
    if (!name) { message.warning('Please enter a name.'); return; }
    setCloning(true);
    try {
      const res = await api.post(`/api/eval-forms/${cloneSource.ID}/clone`, { name });
      message.success('Copy created — opening editor.');
      setCloneOpen(false);
      navigate(`/eval-forms/${res.data.id}/edit`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to copy form.');
    } finally {
      setCloning(false);
    }
  };

  const openRename = (record) => {
    setRenameSource(record);
    setRenameName(record.Name);
  };
  const versionCount = renameSource ? forms.filter((f) => f.Name === renameSource.Name).length : 0;

  const handleRename = async () => {
    const name = renameName.trim();
    if (!name) { message.warning('Please enter a name.'); return; }
    setRenaming(true);
    try {
      const res = await api.post(`/api/eval-forms/${renameSource.ID}/rename`, { name });
      message.success(res.data?.count ? `Renamed ${res.data.count} version${res.data.count === 1 ? '' : 's'}.` : 'Name unchanged.');
      setRenameSource(null);
      fetchForms();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to rename form.');
    } finally {
      setRenaming(false);
    }
  };

  const handleDeactivate = async (formId) => {
    try {
      await api.post(`/api/eval-forms/${formId}/deactivate`);
      message.success('Form set to inactive.');
      fetchForms();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to deactivate form.');
    }
  };

  const handleDelete = async (formId) => {
    try {
      await api.delete(`/api/eval-forms/${formId}`);
      message.success('Form deleted.');
      fetchForms();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete.');
    }
  };

  const iconBtn = (tip, icon, onClick, props = {}) => (
    <Tooltip title={tip}>
      <Button size="small" icon={icon} onClick={onClick} {...props} />
    </Tooltip>
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name',
      render: (name, record) => (
        <a onClick={() => navigate(`/eval-forms/${record.ID}/edit`)}>{name}</a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 100,
      render: (s) => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>,
    },
    {
      title: 'Ver',
      dataIndex: 'Version',
      key: 'Version',
      width: 55,
      render: (v) => v ? `v${v}` : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Qs',
      dataIndex: 'QuestionCount',
      key: 'QuestionCount',
      width: 50,
    },
    {
      title: 'Pts',
      dataIndex: 'TotalPoints',
      key: 'TotalPoints',
      width: 65,
      render: (v) => v ? `${v} pts` : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Type',
      dataIndex: 'form_type',
      key: 'form_type',
      width: 130,
      render: (v) => v === 'error_tracking'
        ? <Tag color="red">Error Tracking</Tag>
        : v === 'helpdesk'
        ? <Tag color="purple">Help Desk</Tag>
        : <Tag color="blue">Evaluation</Tag>,
    },
    {
      title: 'Campaigns',
      dataIndex: 'Campaigns',
      key: 'Campaigns',
      render: (camps) => camps?.length
        ? <Space size={4} wrap>{camps.map((c) => <Tag key={c}>{c}</Tag>)}</Space>
        : <Typography.Text type="secondary">All</Typography.Text>,
    },
    {
      title: 'Created By',
      dataIndex: 'CreatedByName',
      key: 'CreatedByName',
      width: 150,
    },
    {
      title: 'Updated',
      dataIndex: 'UpdatedAt',
      key: 'UpdatedAt',
      width: 135,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.UpdatedAt ?? '').localeCompare(b.UpdatedAt ?? ''),
      defaultSortOrder: 'descend',
    },
    {
      title: '',
      key: 'actions',
      width: 175,
      render: (_, record) => (
        <Space size={4}>
          {/* View / Edit */}
          {canEdit && record.Status === 'draft'
            ? iconBtn('Edit', <EditOutlined />, () => navigate(`/eval-forms/${record.ID}/edit`))
            : iconBtn('View', <EyeOutlined />,  () => navigate(`/eval-forms/${record.ID}/edit`))}

          {/* Publish (draft only, edit only) */}
          {canEdit && record.Status === 'draft' && (
            <Popconfirm
              title="Publish this form?"
              description="It will become available for evaluation assignment."
              onConfirm={() => handlePublish(record.ID)}
              okText="Publish"
            >
              <Tooltip title="Publish">
                <Button size="small" type="primary" icon={<CheckOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}

          {/* Deactivate (published only, edit only) */}
          {canEdit && record.Status === 'published' && (
            <Popconfirm
              title="Set this form to inactive?"
              description="It will no longer be available for new evaluation assignments."
              onConfirm={() => handleDeactivate(record.ID)}
              okText="Deactivate"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Set Inactive">
                <Button size="small" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}

          {/* New Version (published or inactive, edit only) */}
          {canEdit && record.Status !== 'draft' &&
            iconBtn('New Version', <BranchesOutlined />, () => handleNewVersion(record.ID))}

          {/* Rename, all versions (admins) */}
          {canRename && iconBtn('Rename (all versions)', <FontSizeOutlined />, () => openRename(record))}

          {/* Copy / Clone (edit only) */}
          {canEdit && iconBtn('Copy to new form', <CopyOutlined />, () => openClone(record))}

          {/* Delete (draft only, edit only) */}
          {canEdit && record.Status === 'draft' && (
            <Popconfirm
              title="Delete this draft?"
              onConfirm={() => handleDelete(record.ID)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const filteredForms = forms
    .filter((f) => !statusFilter.length || statusFilter.includes(f.Status))
    .filter((f) => !typeFilter.length   || typeFilter.includes(f.form_type));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Evaluation Forms
        </Typography.Title>
        {canEdit && (
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate('evaluation')}>
              New Evaluation Form
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate('error_tracking')}>
              New Error Form
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate('helpdesk')}>
              New Help Desk Form
            </Button>
          </Space>
        )}
      </div>

      <Space style={{ marginBottom: 12 }}>
        <Select
          mode="multiple"
          allowClear
          placeholder="All statuses"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { label: 'Draft',     value: 'draft'     },
            { label: 'Published', value: 'published' },
            { label: 'Inactive',  value: 'inactive'  },
          ]}
          style={{ width: 220 }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="All types"
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { label: 'Evaluation',     value: 'evaluation'     },
            { label: 'Error Tracking', value: 'error_tracking' },
            { label: 'Help Desk',      value: 'helpdesk'       },
          ]}
          style={{ width: 200 }}
        />
      </Space>

      <Table
        rowKey="ID"
        dataSource={filteredForms}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal maskClosable={false}
        title={
          createType === 'error_tracking' ? 'New Error Tracking Form'
          : createType === 'helpdesk' ? 'New Help Desk Form'
          : 'New Evaluation Form'
        }
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={creating}
        okText="Create"
      >
        <div style={{ marginTop: 16 }}>
          <Typography.Text>Form name:</Typography.Text>
          <Input
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onPressEnter={handleCreate}
            placeholder="Enter a unique name"
            style={{ marginTop: 8 }}
            autoFocus
          />
        </div>
      </Modal>

      <Modal maskClosable={false}
        title={`Copy "${cloneSource?.Name}" to a new form`}
        open={cloneOpen}
        onOk={handleClone}
        onCancel={() => setCloneOpen(false)}
        confirmLoading={cloning}
        okText="Create Copy"
      >
        <div style={{ marginTop: 16 }}>
          <Typography.Text>New form name:</Typography.Text>
          <Input
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            onPressEnter={handleClone}
            style={{ marginTop: 8 }}
            autoFocus
          />
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            The copy will be saved as a draft at v1 when published.
          </Typography.Text>
        </div>
      </Modal>

      <Modal maskClosable={false}
        title={`Rename "${renameSource?.Name}"`}
        open={!!renameSource}
        onOk={handleRename}
        onCancel={() => setRenameSource(null)}
        confirmLoading={renaming}
        okText="Rename"
      >
        <div style={{ marginTop: 16 }}>
          <Typography.Text>New form name:</Typography.Text>
          <Input
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onPressEnter={handleRename}
            style={{ marginTop: 8 }}
            autoFocus
          />
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            Renames all {versionCount} version{versionCount === 1 ? '' : 's'} of this form.
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
}
